import { P2GGrid, P2GIcon, P2GLink, P2GText, SvgFix, SvgLink, SvgVan } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import { AchievementType } from '~/apps/smartsend/lib/context/ProfileContext';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';
import MarketplaceAction from '../onboarding/components/marketplace-action';
import BulkAction from '../onboarding/components/bulk-action';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';
import { ActionWrapper } from './action-wrapper';
import { SuperButton } from './super-button';
import ConcernsSmartAction from './concerns';
import { ConcernType } from '../concerns/Concern';

type ItemProps = {
  prompts: number;
  contextualPrompt: { intent: string; key?: AchievementType };
  i: number;
  currentOption: ApplierOption | null;
  onHide: (keys: AchievementType[]) => void;
  onAchievementClick: { [K in AchievementType]?: () => void };
  hide: (keys: AchievementType[]) => void;
  onSend: () => void;
  onGenericFixItemsClick: () => void;
  onResetFilter: () => void;
  onBulkFixItemsBulkClick: (a: ApplierOption) => void;
  onBulkFixItemsClick: (a: ApplierOption) => void;
  removeOutstandingActions: (keys: string[]) => void;
  limitToConcernTypes?: ConcernType[];
};

export const SmartAction = ({
  contextualPrompt,
  limitToConcernTypes,
  i,
  currentOption,
  prompts,
  onHide,
  onAchievementClick,
  hide,
  onSend,
  onGenericFixItemsClick,
  onBulkFixItemsBulkClick,
  onResetFilter,
  onBulkFixItemsClick,
  removeOutstandingActions
}: ItemProps) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const variantToUse = i === 0 ? 'primary' : 'tertiary';

  const titleKey = useMemo(
    () => sanitiseKey(`SMARTACTIONS_${contextualPrompt.intent}_TITLE`),
    [contextualPrompt.intent]
  );

  const descriptionKey = useMemo(
    () => sanitiseKey(`SMARTACTIONS_${contextualPrompt.intent}_DESCRIPTION`),
    [contextualPrompt.intent]
  );

  const buttonKey = useMemo(
    () => sanitiseKey(`SMARTACTIONS_${contextualPrompt.intent}_BUTTON`),
    [contextualPrompt.intent]
  );

  switch (contextualPrompt.intent) {
    case 'SEND_FIRST_PARCEL':
      return (
        <ActionWrapper
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          prompts={prompts}
          type="BOOK"
        >
          <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item>
              <P2GIcon size="sm">
                <SvgVan />
              </P2GIcon>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GGrid container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
                <P2GGrid item>
                  <P2GText>
                    <strong>{t(descriptionKey, 'Some items are ready to send')} </strong>{' '}
                    <P2GLink onClick={() => onHide([contextualPrompt.key])}>{t('HIDE_LINK', 'Hide')}</P2GLink>
                  </P2GText>
                </P2GGrid>
                <P2GGrid item>
                  <SuperButton
                    onClick={() => {
                      onAchievementClick[contextualPrompt.key]?.();
                      hide(['SmartSendActionFirstParcelSent']);
                    }}
                    variant="primary"
                  >
                    {t(buttonKey, 'Send')}
                  </SuperButton>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </ActionWrapper>
      );

    case 'LINK_ACCOUNT':
      return (
        <ActionWrapper
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          prompts={prompts}
        >
          <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item>
              <P2GIcon size="sm">
                <SvgLink />
              </P2GIcon>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GGrid container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
                <P2GGrid item>
                  <P2GText>
                    <strong>{t('LINK_ACCOUNT_DESCRIPTION', 'Link your first eCommerce account')}</strong>{' '}
                    <P2GLink onClick={() => onHide([contextualPrompt.key])}>{t('HIDE_LINK', 'Hide')}</P2GLink>
                  </P2GText>
                </P2GGrid>
                <P2GGrid item textAlign="center">
                  <SuperButton
                    onClick={() => {
                      onAchievementClick[contextualPrompt.key]?.();
                      hide(['SmartSendSetupAccountScreen']);
                    }}
                    variant={variantToUse}
                  >
                    {t(buttonKey, 'Link')}
                  </SuperButton>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </ActionWrapper>
      );

    case 'LINK_TOO':
      return (
        <ActionWrapper
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          prompts={prompts}
        >
          <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item>
              <P2GIcon size="sm">
                <SvgLink />
              </P2GIcon>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GGrid container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
                <P2GGrid item>
                  <P2GText>
                    <strong>{t(titleKey, 'Do you want to link any of your eCommerce accounts?')}</strong>{' '}
                    <P2GLink onClick={() => onHide([contextualPrompt.key])}>{t('HIDE_LINK', 'Hide')}</P2GLink>
                  </P2GText>
                </P2GGrid>
                <P2GGrid item textAlign="center">
                  <SuperButton
                    onClick={() => {
                      onAchievementClick[contextualPrompt.key]?.();
                      hide(['SmartSendSetupAccountScreen']);
                    }}
                    variant={variantToUse}
                  >
                    {t(buttonKey, 'Link')}
                  </SuperButton>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </ActionWrapper>
      );

    case 'READY':
      return (
        <ActionWrapper
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          prompts={prompts}
          type="BOOK"
        >
          <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item>
              <P2GIcon size="sm" palette="primary">
                <SvgVan />
              </P2GIcon>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GGrid container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
                <P2GGrid item flexGrow={1}>
                  <P2GText>
                    <strong>{t(descriptionKey, 'Get labels for orders that are ready to send')}</strong>
                  </P2GText>
                </P2GGrid>
                <P2GGrid item>
                  <SuperButton onClick={onSend} variant="primary">
                    {t(buttonKey, 'Book')}
                  </SuperButton>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </ActionWrapper>
      );

    case 'FIX':
      return (
        <ActionWrapper
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          prompts={prompts}
        >
          <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
            <P2GGrid item>
              <P2GIcon size="sm">
                <SvgFix />
              </P2GIcon>
            </P2GGrid>
            <P2GGrid item flexGrow={1}>
              <P2GGrid container justifyContent="space-between" alignItems="center" spacing={1} wrap="nowrap">
                <P2GGrid item>
                  <P2GText>
                    <strong>{t(descriptionKey, 'Prepare orders to ship')}</strong>
                  </P2GText>
                </P2GGrid>
                <P2GGrid item>
                  <SuperButton onClick={onGenericFixItemsClick} variant={variantToUse}>
                    {t(buttonKey, 'Prepare')}
                  </SuperButton>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </ActionWrapper>
      );

    case 'CONCERNS':
      return (
        <ActionWrapper
          prompts={prompts}
          key={contextualPrompt.intent}
          index={i}
          fade={!!currentOption}
          isActive={false}
          type="CONCERN"
        >
          <ConcernsSmartAction
            descriptionKey={descriptionKey}
            buttonKey={buttonKey}
            variantToUse={variantToUse}
            limitToConcernTypes={limitToConcernTypes}
          />
        </ActionWrapper>
      );

    default:
      if (contextualPrompt.intent.includes(':Add')) {
        return (
          <ActionWrapper
            prompts={prompts}
            isActive={currentOption === contextualPrompt.intent}
            key={contextualPrompt.intent}
            index={i}
            fade={currentOption && currentOption !== contextualPrompt.intent}
          >
            <BulkAction
              buttonVariant={variantToUse}
              onBulkFix={onBulkFixItemsBulkClick}
              onResetFilter={onResetFilter}
              action={contextualPrompt.intent as ApplierOption}
              isActive={currentOption === contextualPrompt.intent}
              onActionClick={onBulkFixItemsClick}
            />
          </ActionWrapper>
        );
      }

      if (contextualPrompt.intent.includes('LINK_')) {
        const integration = contextualPrompt.intent.split('LINK_')[1];
        return (
          <ActionWrapper
            key={contextualPrompt.intent}
            index={i}
            fade={!!currentOption}
            isActive={false}
            prompts={prompts}
          >
            <MarketplaceAction
              integration={integration}
              onHide={() => removeOutstandingActions([integration])}
              onComplete={() => removeOutstandingActions([integration])}
            />
          </ActionWrapper>
        );
      }

      return null;
  }
};

export default SmartAction;
